import type SuggestionService from './SuggestionService';
import {
    type CorporationInfo,
    type VehicleInfo,
    type VehicleType,
    type TechnicalStop,
    type VehicleImage,
} from './types';
import { VehicleImageApiVehicleImagesGet } from '@clients/ContentApi';
import { toAbsoluteSrc } from '@store/content';

export default abstract class Trip {
    /**
     * final destination of trip (just bus have)
     *
     * @type {(string | null)}
     * @memberof Trip
     */
    finalDestination: string | null = null;

    /**
     * carrier code of current trip
     *
     * @type {(string | null)}
     * @memberof Trip
     */
    carrierCode: string | null = null;

    /**
     * stops between in trip
     *
     * @type {string[]}
     * @memberof Trip
     */
    tripMidStops: string[] = [];

    /**
     * Corporation info of current trip
     *
     * @type {CorporationInfo}
     * @memberof Trip
     */
    corporationInfo: CorporationInfo = {
        logoUrl: '',
        logoFallbackUrl: '',
        title: '',
        code: '',
    };

    vehicleImages: VehicleImage[] = [];

    vehicleImageLoadingStatus: 'None' | 'Loading' | 'Done' = 'None';

    async loadVehicleImages() {
        if (this.vehicleImageLoadingStatus !== 'None') {
            return;
        }
        const type = this.vehicleType
            ? this.vehicleType.substring(0, 1).toUpperCase() +
              this.vehicleType.substring(1).toLowerCase()
            : undefined;
        try {
            this.vehicleImageLoadingStatus = 'Loading';
            const { data } = await VehicleImageApiVehicleImagesGet({
                origin: this.originCode,
                destination: this.destinationCode,
                corporation: this.corporationInfo.title,
                vehicle: this.vehicleInfo.type,
                type,
                vehicleCode: this.vehicleInfo.code,
                corporationCode: this.corporationInfo.code,
            });
            this.vehicleImages = data.map(i => ({
                alt: i.alternativeText || '',
                urls: {
                    thumbnail: toAbsoluteSrc(i.formats?.thumbnail?.url),
                    small: toAbsoluteSrc(i.formats?.small?.url),
                    original: toAbsoluteSrc(i.url),
                },
            }));
        } catch (e) {
            reportException(e);
        } finally {
            this.vehicleImageLoadingStatus = 'Done';
        }
    }

    operatingWarning: string | null = null;

    /**
     * extra info for show on trip
     *
     * @type {string[]}
     * @memberof Trip
     */
    get tripExtraInfo(): string[] {
        return this.vehicleInfo.description
            ? [this.vehicleInfo.description]
            : [];
    }

    /**
     * Returns the time of Departure from origin in 'hh:mm' format
     *
     * @type {string}
     * @memberof Trip
     */
    abstract departureTime: string;

    /**
     * Returns the number of stops in the main way
     *
     * @type {number}
     * @memberof Trip
     */
    abstract get stops(): number;

    /**
     * Returns the time of the arrival to destination in 'hh:mm' format
     *
     * @type {string}
     * @memberof Trip
     */
    abstract arrivalTime: string;

    /**
     * Returns the origin of the trip
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get origin(): string;

    /**
     * Returns the name of origin city
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get originCityName(): string;

    /**
     * Returns the code of origin
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get originCode(): string;

    /**
     * Returns the name of origin terminal
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get originTerminal(): string;

    /**
     * Returns the destination of the trip
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get destination(): string;

    /**
     * Returns the name of destination city
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get destinationCityName(): string;

    /**
     * Returns the code of destination
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get destinationCode(): string;

    /**
     * Returns the name of destination terminal
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get destinationTerminal(): string;

    get dateString() {
        return dayConfig
            .js(this.departureDateTime)
            .format(formatter({ format: 'long', includeWeekday: true }));
    }

    /**
     * Returns the departure time and date
     *
     * @type {Date}
     * @deprecated unsafe, don't use this as this may have non-deterministic behaviour
     * @memberof Trip
     */
    abstract departureDateTime: Date;

    /**
     * Returns the arrival time and date
     *
     * @type {Date}
     * @deprecated unsafe, don't use this as this may have non-deterministic behaviour
     * @memberof Trip
     */
    abstract arrivalDateTime: Date;

    /**
     * Returns the vehicle of the trip
     * Can be plane, train or bus
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get vehicleType(): VehicleType;

    /**
     * tells destination going to have warnings or not
     *
     * @type {boolean}
     * @memberof Trip
     */
    abstract get destinationHasWarning(): boolean;

    /**
     * stops in the middle of trips , which user can not exist the Vehicle
     *
     * @type {boolean}
     * @memberof Trip
     */
    technicalStops: TechnicalStop[] = [];

    /**
     * image info of this trip
     *
     * @readonly
     * @abstract
     * @type {VehicleInfo}
     * @memberof Trip
     */
    vehicleInfo: VehicleInfo = {
        title: '',
        type: '',
        code: undefined,
    };

    /**
     * Returns the unique key, to be used in rendering list of trips.
     *
     * @type {number}
     * @memberof Trip
     */
    abstract get key(): string;

    /**
     * Returns the Number of service
     *
     * @type {string}
     * @memberof Trip
     */
    abstract get serviceNumber(): string;

    /**
     * Returns the ID of service
     *
     * @type {number}
     * @memberof Trip
     */
    abstract get serviceId(): number;

    /**
     * Indicates whether city codes should be shown or not
     *
     * @type {boolean}
     * @memberof Trip
     */
    abstract get showCityCodes(): boolean;

    /**
     * suggestion service list of this trip
     *
     * @type {SuggestionService[]}
     * @memberof Trip
     */
    suggestionServices: SuggestionService[] = [];

    /**
     * Returns the difference between the date of departure and the date of arrival in days
     *
     * @type {number}
     * @memberof Trip
     */
    get nextDayCount(): number {
        return dayConfig
            .gregory(this.arrivalDateTime)
            .startOf('day')
            .diff(
                dayConfig.gregory(this.departureDateTime).startOf('day'),
                'day'
            );
    }

    /**
     * Returns the duration of trip
     *
     * @type {number}
     * @memberof Trip
     */
    get duration(): number {
        return Math.floor(
            (this.arrivalDateTime.getTime() -
                this.departureDateTime.getTime()) /
                1000
        );
    }
}
